<script setup lang="ts">
  import { ref, watch, computed, onUnmounted } from 'vue'
  import {
    type TForm,
    type Document,
    type UnifiedDocumentTemplate,
    type FormField,
    UnifiedDocumentTemplatesService,
    AssignedToEnum
  } from '@/types/generated'
  import { useNewPdfStore } from '@/store/newPdfStore'
  import { useSignatures } from '@/store/signatureStore'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { VuePDF, type AnnotationEventPayload } from '@tato30/vue-pdf'
  import type { PageViewport } from 'pdfjs-dist'
  import { useTranslation } from '@/composables/useTranslation'
  import { usePdfFormValidation } from '@/composables/usePdfFormValidation'
  import {
    buildPrefillDataFromFormFields,
    prefillFormFields as prefillFormFieldsUtil
  } from '@/utils/entityPrefillHelpers'
  import InboxEmpty from '@/components/svg/Illustrations/InboxEmpty.vue'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
  } from '@/components/ui/dialog'
  import GenericPdfNav from '@/components/pdf/genericPdfNav.vue'
  import { Button } from '@/components/ui/button'
  import { ScrollArea } from '@/components/ui/scroll-area'
  import { Input } from '@/components/ui/input'
  import {
    Plus,
    Loader2,
    Check,
    Search,
    Pencil,
    MailCheck,
    RotateCcw,
    X,
    ChevronLeft,
    ChevronRight
  } from 'lucide-vue-next'
  import SignModal from '@/components/pdf/SignModal.vue'
  import FormFieldOverlayItem from '@/components/forms/FormFieldOverlayItem.vue'
  import SignModalV3 from '@/components/pdf/SignModalV3.vue'
  import type { DocumentFormState } from '@/views/app/cases/legacy/LegacyCaseDetailPage.vue'

  const props = defineProps<{
    show: boolean
    title?: string
    description?: string
    initialSelectedDocuments?: UnifiedDocumentTemplate[]
    initialFieldValues?: Map<string, Record<string, any>>
    documentFormState?: Map<string, DocumentFormState>
  }>()

  const emit = defineEmits<{
    (e: 'update:show', value: boolean): void
    (e: 'select', documents: UnifiedDocumentTemplate[]): void
    (e: 'field-value-changed', payload: { documentId: string; fieldId: string; value: any }): void
    (
      e: 'validation-changed',
      payload: { documentId: string; fieldId: string; error: string | undefined }
    ): void
  }>()

  const { t } = useTranslation({
    en: {
      attach: 'Attach',
      cancel: 'Cancel',
      page: 'Page {from} of {to}',
      preview: 'Select a document template from the list to preview',
      search: 'Search document templates...',
      sentDate: 'Sent on {date}',
      remove: 'Remove',
      title: 'Attach Documents ({docCount})',
      attached: 'Attached',
      done: 'Done',
      error: 'Error loading document',
      loading: 'Loading...',
      loadingDocument: 'Loading document...',
      reset: 'Reset',
      previous: 'Previous',
      next: 'Next',
      noDocuments: 'No documents found',
      loadingDocuments: 'Loading documents...',
      lawyerFields: 'Lawyer fields',
      clientFields: 'Client fields'
    },
    de: {
      attach: 'Anhängen',
      cancel: 'Abbrechen',
      page: 'Seite {from} von {to}',
      preview: 'Wählen Sie eine Dokumentenvorlage aus der Liste aus, um eine Vorschau anzuzeigen',
      remove: 'Entfernen',
      search: 'Dokumentenvorlagen durchsuchen...',
      sentDate: 'Am {date} gesendet',
      title: 'Dokumente anhängen ({docCount})',
      attached: 'Angehängt',
      done: 'Fertig',
      error: 'Dokument konnte nicht geladen werden',
      loading: 'Laden...',
      loadingDocument: 'Dokument wird geladen...',
      reset: 'Zurücksetzen',
      previous: 'Zurück',
      next: 'Weiter',
      noDocuments: 'Keine Dokumente gefunden',
      loadingDocuments: 'Dokumente werden geladen...',
      lawyerFields: 'Anwaltsfelder',
      clientFields: 'Mandantenfelder'
    }
  })

  // Store
  const pdfStore = useNewPdfStore()
  const signatureStore = useSignatures()
  const caseStore = useCaseStore()

  // State
  const previewingDocument = ref<UnifiedDocumentTemplate | null>(null)
  const selectedDocuments = ref<UnifiedDocumentTemplate[]>([])
  const resettingForm = ref(false)
  const search = ref('')

  // Pagination state
  const currentPage = ref(1)
  const totalPages = ref(1)
  const totalCount = ref(0)
  const documents = ref<UnifiedDocumentTemplate[]>([])
  const isLoadingDocuments = ref(false)
  const searchDebounceTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
  const pageSize = 20

  // Form fields state - store per document ID
  const documentFormFields = ref<Map<string, any[]>>(new Map())
  const localDocumentFieldValues = ref<Map<string, Record<string, any>>>(new Map())

  // Signature modal state
  const showSignatureModal = ref(false)
  const currentSignatureField = ref<any>(null)

  watch(
    () => props.initialSelectedDocuments,
    (newDocs) => {
      if (newDocs) {
        // Important: Create a fresh array instead of reference
        selectedDocuments.value = JSON.parse(JSON.stringify(newDocs))
      } else {
        selectedDocuments.value = []
      }
    },
    { immediate: true }
  )

  const isDocumentAttached = (docId: string): boolean => {
    return props.documentFormState?.has(docId) ?? false
  }

  watch(
    () => props.initialFieldValues,
    (newFieldValues) => {
      if (newFieldValues && newFieldValues.size > 0) {
        newFieldValues.forEach((values, docId) => {
          if (!isDocumentAttached(docId)) {
            localDocumentFieldValues.value.set(docId, values)
          }
        })
      }
    },
    { immediate: true }
  )

  // Computed
  const caseRecord = computed(() => caseStore.caseRecord)
  const pdfState = computed(() => pdfStore.selectedPdfs.get(previewingDocument.value?.id || ''))
  const pdfStateAnnotationMap = computed(() => {
    const docId = previewingDocument.value?.id || ''
    // Don't show annotations for PDF forms - we use FormFieldOverlayItem instead
    if (previewingDocument.value?.template_type === 'pdf_form') {
      return {}
    }
    return pdfStore.getAnnotationsMap(docId)
  })

  // Check if we should show annotation layer (only for non-PDF forms)
  const shouldShowAnnotations = computed(() => {
    return previewingDocument.value?.template_type !== 'pdf_form'
  })

  const filteredDocuments = computed(() => {
    return documents.value
  })
  // Prefill form fields based on tag and tag_relationship
  // This dynamically extracts only the fields needed from case data based on form field tags
  const prefillFormFields = (docId: string, formFields: any[]) => {
    if (isDocumentAttached(docId)) {
      // If document is attached, parent handles prefilling
      return
    }

    const existingFieldValues = localDocumentFieldValues.value.get(docId) || {}

    // Build prefill data dynamically based on what fields are actually used in this form
    // No hardcoding - it extracts field names from tags and looks them up in case data
    const prefillDataByRelationship = buildPrefillDataFromFormFields(
      formFields,
      caseRecord.value?.data
    )

    // Get prefilled values using the utility function
    const prefilledValues = prefillFormFieldsUtil(
      formFields,
      prefillDataByRelationship,
      true // only prefill empty fields
    )

    // Merge with existing values (existing values take precedence)
    const mergedValues = { ...prefilledValues, ...existingFieldValues }

    localDocumentFieldValues.value.set(docId, mergedValues)
  }

  // Get form fields for the currently previewing document
  const currentDocumentFormFields = computed(() => {
    if (!previewingDocument.value) return []
    return documentFormFields.value.get(previewingDocument.value.id) || []
  })

  const hasFormFields = computed(() => {
    return currentDocumentFormFields.value.length > 0
  })

  // Methods
  const fetchDocuments = async (page: number = 1, searchQuery?: string) => {
    isLoadingDocuments.value = true
    try {
      const offset = (page - 1) * pageSize
      const response = await UnifiedDocumentTemplatesService.unifiedDocumentTemplatesList({
        limit: pageSize,
        offset,
        search: searchQuery
      })

      let fetchedDocuments = response.results || []

      // Only include selected documents when there's no active search
      // This ensures search results are clean and only show documents matching the search
      if (
        !searchQuery &&
        props.initialSelectedDocuments &&
        props.initialSelectedDocuments.length > 0
      ) {
        const fetchedIds = new Set(fetchedDocuments.map((doc) => doc.id))
        const missingSelected = props.initialSelectedDocuments.filter(
          (doc) => !fetchedIds.has(doc.id)
        )

        if (missingSelected.length > 0) {
          fetchedDocuments = [...fetchedDocuments, ...missingSelected]
        }
      }

      documents.value = fetchedDocuments
      totalCount.value = response.count || 0
      totalPages.value = Math.ceil(totalCount.value / pageSize)
      currentPage.value = page
    } catch (error) {
      console.error('Error fetching documents:', error)
      documents.value = []
      totalCount.value = 0
      totalPages.value = 1
    } finally {
      isLoadingDocuments.value = false
    }
  }

  const handleSearch = (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    search.value = value

    if (searchDebounceTimeout.value) {
      clearTimeout(searchDebounceTimeout.value)
    }

    searchDebounceTimeout.value = setTimeout(async () => {
      currentPage.value = 1
      await fetchDocuments(1, value || undefined)
    }, 300)
  }

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
      fetchDocuments(page, search.value || undefined)
    }
  }

  const getDocumentSentDate = (doc: UnifiedDocumentTemplate): string | null => {
    // Check if document was previously sent
    const cachedDate = pdfStore.getSentDate(doc.id)
    if (cachedDate) {
      return cachedDate
    }

    // For PDF forms, check in caseRecord.pdf_form_submissions
    if (doc.template_type === 'pdf_form') {
      const pdfSubmissions = caseStore.caseRecord?.pdf_form_submissions
      if (pdfSubmissions && Array.isArray(pdfSubmissions)) {
        const matchingSubmission = pdfSubmissions.find((submission) => {
          // Match by template uuid or by template name
          const matchByUuid = submission.template?.uuid === doc.id
          const matchByName = submission.template_name === doc.title
          return matchByUuid || matchByName
        })

        if (matchingSubmission) {
          const created_at = new Intl.DateTimeFormat('de-DE').format(
            new Date(matchingSubmission.created_at)
          )
          pdfStore.setSentDate(doc.id, created_at)
          return created_at
        }
      }
    }

    // For legacy forms, check in caseStore.documents
    const caseDocuments = caseStore.documents

    if (!caseDocuments || !Array.isArray(caseDocuments)) {
      return null
    }

    const sentDocuments = caseDocuments.filter(
      (caseDoc: Document) => caseDoc.document_type === 'Dokumentenvorlage'
    )

    const currentDocument = sentDocuments.find((sentDoc) => {
      return sentDoc.title === doc.title
    })

    if (currentDocument) {
      const created_at = new Intl.DateTimeFormat('de-DE').format(
        new Date(currentDocument.created_at)
      )
      pdfStore.setSentDate(doc.id, created_at)
      return created_at
    }

    return null
  }

  const formatDocumentName = (doc: UnifiedDocumentTemplate): string => {
    return doc.title || ''
  }

  const isDocumentSelected = (docId: string) => {
    return selectedDocuments.value.some((d) => d.id === docId)
  }

  const handleDocumentPreview = async (doc: UnifiedDocumentTemplate) => {
    previewingDocument.value = doc
    signatureStore.setPdfStore('new')
    try {
      // For unified templates, we need to convert to TForm format for the PDF store
      const formDoc = {
        id: doc.id,
        title: doc.title || '',
        description: doc.description || '',
        file: doc.file,
        organisation: doc.created_by?.org || ({} as any),
        created_at: doc.created_at,
        modified_at: doc.modified_at,
        tag: 'MASTER',
        document_name: doc.file,
        due_at: null as any
      } as TForm
      await pdfStore.loadPdf(formDoc)

      // If this is a PDF form, load the form fields
      if (doc.template_type === 'pdf_form' && doc.form_fields) {
        documentFormFields.value.set(doc.id, doc.form_fields)

        // Initialize field values for this document if not already done
        // Skip if document is attached (parent manages values)
        if (!isDocumentAttached(doc.id) && !localDocumentFieldValues.value.has(doc.id)) {
          const initialValues: Record<string, any> = {}
          doc.form_fields.forEach((field: any) => {
            if (field.type === 'checkbox' && field.uuid) {
              initialValues[field.uuid] = false
            }
          })
          localDocumentFieldValues.value.set(doc.id, initialValues)
          // Prefill form fields with case entity data
          prefillFormFields(doc.id, doc.form_fields)
        }

        // Extract signature fields for the signature store
        const pdfState = pdfStore.selectedPdfs.get(doc.id)
        if (pdfState?.loadingTask) {
          try {
            const pdfDoc = await pdfState.loadingTask.promise
            if (pdfDoc && typeof pdfDoc.getFieldObjects === 'function') {
              const fieldObjects = await pdfDoc.getFieldObjects()
              if (fieldObjects) {
                await signatureStore.updateAll(fieldObjects)
                await signatureStore.findAllSignatureFields(fieldObjects)
              }
            }
          } catch (error) {
            console.warn('Error extracting signature fields:', error)
          }
        }
        const lawyerRequiredFields = (doc.form_fields as FormField[]).filter(
          (f) => f.required && f.assigned_to === AssignedToEnum.LAWYER
        )
        validateAllFields(lawyerRequiredFields)
      }
    } catch (error) {
      console.error('Error loading document:', error)
    }
  }

  // Helper function to attach field values to form_fields before emitting
  const attachFieldValuesToDocuments = () => {
    return selectedDocuments.value.map((doc) => {
      if (doc.template_type === 'pdf_form' && doc.form_fields) {
        let fieldValues: Record<string, any> = {}

        if (isDocumentAttached(doc.id)) {
          // Use parent's state
          const docState = props.documentFormState?.get(doc.id)
          if (docState) {
            docState.fieldValues.forEach((value, key) => {
              fieldValues[key] = value
            })
          }
        } else {
          fieldValues = localDocumentFieldValues.value.get(doc.id) || {}
        }

        const fieldsWithValues = doc.form_fields.map((field: any) => {
          const fieldId = field.uuid as string
          // Use the edited value if it exists (even if empty string), otherwise use original value
          let value
          if (fieldId && fieldId in fieldValues) {
            value = fieldValues[fieldId]
          } else {
            // For checkbox fields, default to false instead of empty string
            value = field.type === 'checkbox' ? (field.value ?? false) : (field.value ?? '')
          }
          return {
            ...field,
            value
          }
        })
        return {
          ...doc,
          form_fields: fieldsWithValues
        }
      }
      return doc
    })
  }

  const emitSelected = () => {
    const documentsWithValues = attachFieldValuesToDocuments()
    emit('select', documentsWithValues)
  }

  const handleDocumentAdd = (doc: UnifiedDocumentTemplate) => {
    if (!doc) return
    if (!isDocumentSelected(doc.id)) {
      selectedDocuments.value = [...selectedDocuments.value, doc]

      // If this is a PDF form, prefill it if not already done
      if (doc.template_type === 'pdf_form' && doc.form_fields) {
        if (!documentFormFields.value.has(doc.id)) {
          documentFormFields.value.set(doc.id, doc.form_fields)
        }
        if (!isDocumentAttached(doc.id) && !localDocumentFieldValues.value.has(doc.id)) {
          // Initialize with default values for checkbox fields
          const initialValues: Record<string, any> = {}
          doc.form_fields.forEach((field: any) => {
            if (field.type === 'checkbox' && field.uuid) {
              initialValues[field.uuid] = false
            }
          })
          localDocumentFieldValues.value.set(doc.id, initialValues)
          // Prefill form fields with case entity data
          prefillFormFields(doc.id, doc.form_fields)
        }
      }

      const documentsWithValues = attachFieldValuesToDocuments()
      emit('select', documentsWithValues)
    }
  }

  const handleDocumentRemove = (doc: UnifiedDocumentTemplate) => {
    selectedDocuments.value = selectedDocuments.value.filter((d) => d.id !== doc.id)
    // Keep local field values in case user re-adds the document
    // Parent will handle cleanup of attached document values
    const documentsWithValues = attachFieldValuesToDocuments()
    emit('select', documentsWithValues)
  }

  const handleSubmit = () => {
    emitSelected()
    emit('update:show', false)
  }

  const setPage = (page: number) => {
    if (previewingDocument.value && pdfStore.selectedPdfs.get(previewingDocument.value.id)) {
      pdfStore.setCurrentPage(previewingDocument.value.id, page)
    }
  }

  const onPdfLoaded = async (e: PageViewport) => {
    const pdf = await pdfState.value?.loadingTask?.promise
    if (pdf) {
      pdfStore.onPdfLoaded(previewingDocument.value?.id || '', pdf.numPages)
      if (!pdfState.value?.currentPage || pdfState.value.currentPage < 1) {
        pdfStore.setCurrentPage(previewingDocument.value?.id || '', 1)
      }
    }
  }

  const onAnnotation = (payload: AnnotationEventPayload) => {
    // Don't process annotations for PDF forms - we use FormFieldOverlayItem instead
    if (previewingDocument.value?.template_type === 'pdf_form') {
      // Still handle link annotations for navigation
      if (payload.type === 'link' && payload.data.url) {
        window.open(payload.data.url, '_blank')
      }
      return
    }

    // Handle link annotations by opening in new tab
    if (payload.type === 'link') {
      if (payload.data.url) {
        window.open(payload.data.url, '_blank')
      }
      return
    }
    pdfStore.updateAnnotation(previewingDocument.value?.id || '', payload)
    pdfStore.setManuallyEdited(previewingDocument.value?.id || '', true)
  }

  const resetAnnotation = (doc: UnifiedDocumentTemplate) => {
    if (!doc) return
    resettingForm.value = true
    pdfStore.resetAnnotations(doc.id)
    resettingForm.value = false
  }

  // Handle field value updates
  const updateFieldValue = (fieldId: string | undefined, value: any) => {
    if (!fieldId || !previewingDocument.value) return

    const docId = previewingDocument.value.id

    if (isDocumentAttached(docId)) {
      emit('field-value-changed', {
        documentId: docId,
        fieldId: fieldId,
        value: value
      })
    } else {
      const currentValues = localDocumentFieldValues.value.get(docId) || {}
      currentValues[fieldId] = value
      localDocumentFieldValues.value.set(docId, currentValues)
    }

    const field = currentDocumentFormFields.value.find((f) => f.uuid === fieldId)
    if (field) {
      validateSingleField(field)
    }
  }

  // Get field value
  const getFieldValue = (fieldId: string | undefined) => {
    if (!fieldId || !previewingDocument.value) return ''
    const docId = previewingDocument.value.id

    const field = currentDocumentFormFields.value.find((f) => f.uuid === fieldId)
    const isCheckbox = field?.type === 'checkbox'

    if (isDocumentAttached(docId)) {
      const docState = props.documentFormState?.get(docId)
      const value = docState?.fieldValues.get(fieldId)
      return value !== undefined ? value : isCheckbox ? false : ''
    } else {
      const values = localDocumentFieldValues.value.get(docId) || {}
      const value = values[fieldId]
      return value !== undefined ? value : isCheckbox ? false : ''
    }
  }

  // Create validation composable without external errors initially
  // We'll handle validation manually for proper reactivity
  const {
    validateSingleField: validateSingleFieldBase,
    getFieldError: getFieldErrorBase,
    validateAllFields: validateAllFieldsBase,
    clearErrors
  } = usePdfFormValidation(getFieldValue)

  // Wrapper functions that handle external vs local errors
  const validateSingleField = (field: FormField) => {
    if (!previewingDocument.value) return
    const docId = previewingDocument.value.id

    // Only validate lawyer-assigned fields; client fields are filled later by the client
    if (field.assigned_to !== AssignedToEnum.LAWYER) return

    validateSingleFieldBase(field)

    if (isDocumentAttached(docId) && field.uuid) {
      const error = getFieldErrorBase(field.uuid)
      emit('validation-changed', {
        documentId: docId,
        fieldId: field.uuid,
        error: error
      })
    }
  }

  const getFieldError = (fieldId?: string): string | undefined => {
    if (!fieldId || !previewingDocument.value) return undefined
    const docId = previewingDocument.value.id

    if (isDocumentAttached(docId)) {
      const docState = props.documentFormState?.get(docId)
      return docState?.validationErrors.get(fieldId)
    } else {
      return getFieldErrorBase(fieldId)
    }
  }

  const validateAllFields = (fields: FormField[]): boolean => {
    if (!previewingDocument.value) return true
    const docId = previewingDocument.value.id

    const isValid = validateAllFieldsBase(fields)

    if (isDocumentAttached(docId)) {
      fields.forEach((field) => {
        if (field.uuid) {
          const error = getFieldErrorBase(field.uuid)
          emit('validation-changed', {
            documentId: docId,
            fieldId: field.uuid,
            error: error
          })
        }
      })
    }

    return isValid
  }

  // Handle signature field click
  const handleSignatureClick = (field: any) => {
    currentSignatureField.value = field
    // Only open the new SignModalV3 for PDF form fields
    showSignatureModal.value = true
    // Do not set signatureStore.showSignModal - that's for legacy forms
  }

  // Get signature data for a field
  const getSignatureData = (field: any) => {
    if (!previewingDocument.value || !field?.uuid) return ''

    const docId = previewingDocument.value.id

    if (isDocumentAttached(docId)) {
      const docState = props.documentFormState?.get(docId)
      const value = docState?.fieldValues.get(field.uuid)
      if (value) return value
    } else {
      const values = localDocumentFieldValues.value.get(docId) || {}
      if (values[field.uuid]) return values[field.uuid]
    }

    return ''
  }

  // Watch for changes to initialSelectedDocuments
  watch(
    () => props.initialSelectedDocuments,
    (newDocs) => {
      if (newDocs) {
        selectedDocuments.value = [...newDocs]
      }
    }
  )

  // Watch for dialog open/close
  watch(
    () => props.show,
    (show, oldShow) => {
      if (!show && oldShow) {
        // Emit selected documents when closing (for X button and other close triggers)
        emitSelected()

        if (searchDebounceTimeout.value) {
          clearTimeout(searchDebounceTimeout.value)
          searchDebounceTimeout.value = null
        }
        previewingDocument.value = null
        search.value = ''
        currentPage.value = 1

        // Reset signature modal state
        showSignatureModal.value = false
        currentSignatureField.value = null
      } else if (props.initialSelectedDocuments) {
        selectedDocuments.value = [...props.initialSelectedDocuments]
      }

      if (show) {
        fetchDocuments(1)
      }
    }
  )

  onUnmounted(() => {
    if (searchDebounceTimeout.value) {
      clearTimeout(searchDebounceTimeout.value)
      searchDebounceTimeout.value = null
    }
    signatureStore.cleanup()
  })
</script>

<template>
  <Dialog
    :open="show"
    :modal="true"
    @update:open="(v) => emit('update:show', v)"
  >
    <DialogContent class="max-w-7xl p-6">
      <div
        class="flex h-[80vh] w-full flex-col"
        role="dialog"
        aria-modal="true"
      >
        <div>
          <DialogHeader>
            <DialogTitle>
              {{ props.title || t('title', { docCount: totalCount }) }}
            </DialogTitle>
            <DialogDescription>
              {{ props.description }}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div class="flex h-[70vh] flex-1 gap-3 pt-3">
          <!-- Left side: Document list -->
          <div class="flex w-1/3 flex-col">
            <!-- Search input -->
            <div class="relative mb-3">
              <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input
                :model-value="search"
                :placeholder="t('search')"
                class="w-full border border-gray-200 pl-9 shadow-sm focus-visible:ring-2 focus-visible:ring-purple-500"
                @input="handleSearch"
              />
            </div>

            <!-- Document list -->
            <ScrollArea
              class="flex-1 rounded-lg border p-2"
              data-cy="documentsList"
            >
              <div
                v-if="isLoadingDocuments"
                class="flex items-center justify-center py-8"
              >
                <Loader2 class="h-6 w-6 animate-spin text-gray-500" />
                <span class="ml-2 text-sm text-gray-500">{{ t('loadingDocuments') }}</span>
              </div>
              <div
                v-else-if="filteredDocuments.length === 0"
                class="flex h-full flex-col items-center justify-center py-8"
              >
                <p class="mt-2 text-center text-gray-500">
                  {{ t('noDocuments') }}
                </p>
              </div>
              <div
                v-else
                class="divide-y"
              >
                <div
                  v-for="doc in filteredDocuments"
                  :key="doc.id"
                  class="group flex items-center gap-3 border-l-4 p-3 transition-colors hover:bg-gray-50"
                  :class="{
                    'bg-gray-50': previewingDocument?.id === doc.id && !isDocumentSelected(doc.id),
                    'border-l-purple-600': isDocumentSelected(doc.id),
                    'border-l-transparent': !isDocumentSelected(doc.id)
                  }"
                  data-cy="documentItem"
                >
                  <!-- Checkbox section - only handles selection/deselection -->
                  <div
                    class="flex h-5 w-5 items-center justify-center rounded border border-gray-300 transition-colors"
                    :class="{
                      'border-purple-600 bg-purple-600': isDocumentSelected(doc.id)
                    }"
                    data-cy="documentItemCheckbox"
                    @click="
                      isDocumentSelected(doc.id)
                        ? handleDocumentRemove(doc)
                        : handleDocumentAdd(doc)
                    "
                  >
                    <Check
                      v-if="isDocumentSelected(doc.id)"
                      class="h-3.5 w-3.5 text-white"
                    />
                  </div>

                  <!-- Document info section - only handles preview -->
                  <div
                    class="min-w-0 flex-1 cursor-pointer"
                    @click="handleDocumentPreview(doc)"
                  >
                    <div class="flex items-center gap-2">
                      <span
                        class="block max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap text-sm"
                        data-cy="documentItemName"
                        :title="formatDocumentName(doc)"
                      >
                        {{ formatDocumentName(doc) }}
                      </span>
                      <div class="flex flex-shrink-0 gap-1">
                        <div
                          v-if="pdfStore.getManuallyEdited(doc.id)"
                          class="group relative"
                        >
                          <Pencil class="h-3.5 w-3.5 text-gray-500" />
                        </div>
                        <div
                          v-if="getDocumentSentDate(doc)"
                          class="flex items-center gap-1 whitespace-nowrap text-xs text-gray-500"
                        >
                          <MailCheck class="h-4 w-4 text-gray-500" />
                          {{ getDocumentSentDate(doc) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
            <div
              v-if="totalPages > 1"
              class="mt-4 flex items-center justify-start gap-2"
            >
              <Button
                variant="secondary"
                size="sm"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
              >
                <ChevronLeft class="h-4 w-4" />
              </Button>
              <span class="text-sm text-gray-700">
                {{ t('page', { from: currentPage, to: totalPages }) }}
              </span>
              <Button
                variant="secondary"
                size="sm"
                :disabled="currentPage === totalPages"
                @click="goToPage(currentPage + 1)"
              >
                <ChevronRight class="h-4 w-4" />
              </Button>
            </div>
          </div>

          <!-- Right side: PDF preview -->
          <div class="flex w-2/3 flex-col rounded-lg border border-gray-200 shadow-sm">
            <div
              v-if="previewingDocument"
              class="flex h-full flex-col p-3"
            >
              <!-- Document header with title and sent date -->
              <div class="mb-3 flex w-full items-center justify-between gap-4">
                <div class="flex min-w-0 flex-1 items-center gap-4 p-3">
                  <h3 class="truncate text-gray-900">
                    {{ previewingDocument.title }}
                  </h3>

                  <!-- Field Legend on same line as title -->
                  <div
                    v-if="hasFormFields"
                    class="flex flex-shrink-0 items-center gap-4"
                  >
                    <div class="flex items-center gap-2 text-xs">
                      <div class="h-3 w-3 rounded border-2 border-blue-500 bg-blue-100"></div>
                      <span class="text-gray-600">{{ t('lawyerFields') }}</span>
                    </div>
                    <div class="flex items-center gap-2 text-xs">
                      <div class="h-3 w-3 rounded border-2 border-purple-500 bg-purple-100"></div>
                      <span class="text-gray-600">{{ t('clientFields') }}</span>
                    </div>
                  </div>
                </div>

                <div
                  v-if="getDocumentSentDate(previewingDocument)"
                  class="flex flex-shrink-0 items-center gap-1 text-sm text-gray-500"
                >
                  <MailCheck class="h-3.5 w-3.5 text-gray-500" />
                  {{ t('sentDate', { date: getDocumentSentDate(previewingDocument) }) }}
                </div>
              </div>

              <!-- PDF viewer container -->
              <div class="w-full flex-1 overflow-auto rounded-lg border border-gray-200 bg-gray-50">
                <!-- PDF Display -->
                <div class="relative flex min-h-[500px] items-center justify-center p-4">
                  <div
                    v-if="pdfState?.isLoading"
                    class="absolute inset-0 flex h-full items-center justify-center bg-white bg-opacity-80"
                  >
                    <div class="text-gray-500">{{ t('loadingDocument') }}</div>
                  </div>
                  <div
                    v-else-if="pdfState?.error"
                    class="absolute inset-0 flex h-full items-center justify-center"
                  >
                    <div class="text-red-500">{{ t('error') }}</div>
                  </div>
                  <div
                    v-if="pdfState && !pdfState.isLoading && !pdfState.error"
                    class="relative"
                  >
                    <VuePDF
                      ref="vuePDFRef"
                      :key="`pdf-${previewingDocument?.id}-${pdfState?.currentPage || 1}`"
                      on-layer
                      text-layer
                      :annotation-layer="shouldShowAnnotations"
                      :annotations-map="pdfStateAnnotationMap"
                      :scale="1.2"
                      :pdf="pdfState?.loadingTask"
                      :page="pdfState?.currentPage || 1"
                      @loaded="onPdfLoaded"
                      @annotation="onAnnotation"
                    />

                    <!-- Form field overlays for PDF forms -->
                    <FormFieldOverlayItem
                      v-for="field in currentDocumentFormFields.filter(
                        (f) => f.coordinates?.page === (pdfState?.currentPage || 1)
                      )"
                      :key="`field-${field.uuid}-${pdfState?.currentPage || 1}`"
                      :field="field"
                      :zoom-level="1.2"
                      :is-selected="false"
                      :is-editable="false"
                      :is-fillable="true"
                      :field-value="getFieldValue(field.uuid)"
                      :signature-data="field.type === 'signature' ? getSignatureData(field) : ''"
                      :validation-error="getFieldError(field.uuid)"
                      is-filled-by="lawyer"
                      @field-value-change="updateFieldValue"
                      @signature-click="handleSignatureClick"
                    />
                  </div>
                </div>
              </div>

              <div class="mt-3 flex items-center justify-between">
                <!-- Navigation controls -->
                <GenericPdfNav
                  :current-page="pdfState?.currentPage || 1"
                  :total-pages="pdfState?.pages || 1"
                  @update-page="setPage"
                />

                <div class="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    class="gap-2 border border-gray-200 shadow-sm"
                    @click="resetAnnotation(previewingDocument)"
                  >
                    <RotateCcw class="h-4 w-4" />
                    <span>{{ t('reset') }}</span>
                  </Button>
                  <Button
                    v-if="previewingDocument && isDocumentSelected(previewingDocument.id)"
                    variant="ghost"
                    size="sm"
                    class="gap-2 border border-gray-200 text-red-700 shadow-sm"
                    :disabled="!previewingDocument"
                    @click="handleDocumentRemove(previewingDocument)"
                  >
                    <X class="h-4 w-4" />
                    <span>{{ t('remove') }}</span>
                  </Button>
                  <Button
                    v-else
                    variant="ghost"
                    size="sm"
                    class="gap-2 border border-gray-200 shadow-sm"
                    :disabled="!previewingDocument"
                    data-cy="attachBtn"
                    @click="handleDocumentAdd(previewingDocument)"
                  >
                    <Plus class="h-4 w-4" />
                    <span>{{ t('attach') }}</span>
                  </Button>
                </div>
              </div>
            </div>
            <div
              v-else
              class="flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-lg border
                border-gray-200 p-16 font-medium shadow-sm"
            >
              {{ t('preview') }}
              <div style="width: 75%; height: 75%">
                <InboxEmpty />
              </div>
            </div>
          </div>
        </div>

        <!-- Footer with action buttons -->
        <div class="mt-4 flex items-center justify-between border-t pt-4">
          <div class="text-sm text-gray-500">
            {{
              selectedDocuments.length > 0
                ? `${selectedDocuments.length} ${selectedDocuments.length === 1 ? 'Dokument' : 'Dokumente'} angehängt`
                : 'Keine Dokumente angehängt'
            }}
          </div>
          <div class="flex gap-2">
            <Button
              variant="default"
              class="bg-purple-600 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
              size="sm"
              :disabled="selectedDocuments.length === 0"
              data-cy="attachBtn"
              @click="handleSubmit"
            >
              {{ t('done') }}
            </Button>
          </div>
        </div>
      </div>
      <SignModal
        v-if="signatureStore.showSignModal && previewingDocument?.template_type !== 'pdf_form'"
      />

      <!-- Signature Modal V3 for form fields -->
      <SignModalV3
        v-if="showSignatureModal && currentSignatureField"
        v-model:isOpen="showSignatureModal"
        :data="getSignatureData(currentSignatureField)"
        @save="
          (signatureData: any) => {
            updateFieldValue(currentSignatureField.uuid, signatureData)
            showSignatureModal = false
          }
        "
      />
    </DialogContent>
  </Dialog>
</template>

<style scoped>
  /* Empty - styles removed as they were causing positioning issues with form fields */
</style>
